"use client";

import { useState } from "react";
import { buildPostHogRequestContext } from "@/lib/posthog/request-context";
import { captureProductEvent } from "@/lib/posthog/product-events";

const MESSAGE_LIMIT = 2800;
const MESSAGE_MIN_LENGTH = 8;

const FEEDBACK_CATEGORIES = [
  { value: "bug", label: "Bug" },
  { value: "confusing", label: "Confusing" },
  { value: "idea", label: "Idea" },
  { value: "other", label: "Other" },
] as const;

type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]["value"];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const messageLength = message.trim().length;
  const canSubmit =
    category !== null && messageLength >= MESSAGE_MIN_LENGTH && !isSubmitting;

  const submitFeedback = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    setStatus(null);
    let failureCaptured = false;

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          category,
          message: message.trim().slice(0, MESSAGE_LIMIT),
          url: window.location.href,
          ...buildPostHogRequestContext(),
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const reason = payload?.error || "feedback_submit_failed";
        captureProductEvent("feedback_submit_failed", {
          surface: "feedback",
          result: "failure",
          reason,
        });
        failureCaptured = true;
        throw new Error(reason);
      }

      setStatus("Sent");
      setCategory(null);
      setMessage("");
      captureProductEvent("feedback_submitted", {
        surface: "feedback",
        result: "success",
        target: category,
      });
      window.setTimeout(() => {
        setOpen(false);
        setStatus(null);
      }, 1000);
    } catch (submitError) {
      const detail =
        submitError instanceof Error ? submitError.message : "feedback_submit_failed";
      if (!failureCaptured) {
        captureProductEvent("feedback_submit_failed", {
          surface: "feedback",
          result: "failure",
          reason: detail || "feedback_submit_failed",
        });
      }
      setError(`Could not send feedback (${detail}).`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`fixed z-40 ${
        open
          ? "bottom-3 right-3 sm:bottom-6 sm:right-6"
          : "bottom-3 right-3 sm:bottom-auto sm:right-3 sm:top-1/2 sm:-translate-y-1/2"
      }`}
    >
      {open ? (
        <div className="w-[min(92vw,380px)] rounded-3xl border border-white/20 bg-[var(--bg-alt)]/95 p-4 shadow-2xl backdrop-blur">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[var(--muted)]">
              Feedback
            </p>
            <button
              type="button"
              onClick={() => {
                if (isSubmitting) return;
                setOpen(false);
                setError(null);
                setStatus(null);
              }}
              className="rounded-full border border-white/20 bg-white/5 px-3 py-1 text-2xs uppercase tracking-[0.3em] text-[var(--muted)] transition hover:border-white/40 hover:text-[var(--text)]"
            >
              Close
            </button>
          </div>

          <p className="text-xs text-[var(--muted)]">
            Share a bug, confusing moment, or idea. Page and replay context are
            included automatically.
          </p>

          <fieldset className="mt-4">
            <legend className="text-2xs font-semibold uppercase tracking-[0.24em] text-[var(--muted)]">
              What kind of feedback?
            </legend>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {FEEDBACK_CATEGORIES.map((option) => {
                const isSelected = category === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setCategory(option.value)}
                    className={`rounded-full border px-3 py-2 text-2xs font-semibold uppercase tracking-[0.2em] transition ${
                      isSelected
                        ? "border-[var(--accent)] bg-[var(--accent)]/15 text-[var(--accent)]"
                        : "border-white/15 bg-white/5 text-[var(--muted)] hover:border-white/30 hover:text-[var(--text)]"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <label
            htmlFor="feedback-message"
            className="mt-4 block text-2xs font-semibold uppercase tracking-[0.24em] text-[var(--muted)]"
          >
            What were you trying to do, and what got in the way?
          </label>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="I was trying to... but..."
            maxLength={MESSAGE_LIMIT}
            className="mt-2 h-32 w-full resize-none rounded-2xl border border-white/15 bg-black/20 px-3 py-2 text-sm text-[var(--text)] outline-none transition focus:border-[var(--accent)]"
          />

          <div className="mt-3 flex items-center justify-between">
            <p className="text-2xs uppercase tracking-[0.2em] text-[var(--muted)]">
              {messageLength}/{MESSAGE_LIMIT}
              {messageLength > 0 && messageLength < MESSAGE_MIN_LENGTH
                ? ` · add ${MESSAGE_MIN_LENGTH - messageLength} more`
                : ""}
            </p>
            <button
              type="button"
              onClick={() => void submitFeedback()}
              disabled={!canSubmit}
              className="rounded-full border border-[var(--accent)]/50 bg-[var(--accent)]/10 px-4 py-2 text-2xs font-semibold uppercase tracking-[0.3em] text-[var(--accent)] transition hover:border-[var(--accent)] hover:text-[var(--accent-2)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Sending..." : "Send"}
            </button>
          </div>

          {error ? (
            <p className="mt-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              {error}
            </p>
          ) : null}
          {status ? (
            <p className="mt-3 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-100">
              {status}
            </p>
          ) : null}
        </div>
      ) : null}

      {!open ? (
        <button
          type="button"
          onClick={() => {
            captureProductEvent("feedback_opened", {
              surface: "feedback",
              location: "floating_widget",
            });
            setOpen(true);
          }}
          className="rounded-full border border-[var(--accent)]/60 bg-[var(--bg-alt)]/95 px-4 py-3 text-2xs font-semibold uppercase tracking-[0.24em] text-[var(--accent)] shadow-lg backdrop-blur transition hover:border-[var(--accent)] hover:text-[var(--accent-2)] sm:px-2 sm:py-4 sm:[writing-mode:vertical-rl]"
        >
          Feedback
        </button>
      ) : null}
    </div>
  );
}
